import bs58 from 'bs58'
import BN from 'bn.js'
import { getProvider } from './ethUtils'
import {
    OPTIONS_PAUSED_BIT,
    OPTIONS_ALLOW_CREATOR_ROYALTY_BIT,
    OPTIONS_PURCHASE_FEE_SHIFT,
    OPTIONS_PURCHASE_FEE_MASK,
    OPTIONS_MINT_FEE_SHIFT,
    OPTIONS_MINT_FEE_MASK,
    OPTIONS_TRANSACTION_FEE_SHIFT,
    OPTIONS_TRANSACTION_FEE_MASK,
    PRICE_MASK,
    OFFERING_STATE_OPEN,
    MIN_TOKEN_SALE_PRICE_MASK,
    MIN_TOKEN_SALE_PRICE_SHIFT,
    SHARE_PRICE_MASK,
    SHARE_PRICE_SHIFT,
    SHARES_OFFERED_MASK,
    SHARES_OFFERED_SHIFT,
    PLATFORM_SHIFT,
    PLATFORM_MASK,
    TOKEN_PLATFORM,
    PRIVILEGES_SHIFT,
    PRIVILEGES_MASK,
    TOKEN_HAS_PRIVILEGES,
    TOKEN_TYPE_ALBUM,
    TOKEN_TYPE_SHIFT,
    TOKEN_TYPE_MASK,
    TOTAL_SHARE_PERCENT_SHIFT,
    TOTAL_SHARE_PERCENT_MASK,
} from './constants'
import { PAYMENT_TYPES } from 'components/nft/Payments'
export const TX_STATUS = {
    NONE: 0,
    STARTED: 1,
    COMPLETED: 2,
    CONFIRMED: 3,
    FAILED: 4,
}
export const tokenDisclaimer = (action) => {
    return (
        `By clicking 'Yes', you understand that this NFT is stored on the Ethereum blockchain, not on Harmonize
        servers, and that you have sole responsibility for maintaining possession of the NFT. You also ` +
        `represent that you own all rights to any uploaded content that is a part of this NFT. Do you want to ${action} this NFT?`
    )
}

/**
 * This method creates a fake IPFS hash so that we can use the privateMedia fileIds
 * when calculating gas estimates before we actually have publicMedia CIDs.
 * @param {*} value
 * @returns Fakse hash 46 bytes long
 */
export const createIpfsHash = (value) => {
    const bytes = Buffer.from(value)
    const address = bs58.encode(bytes)
    const hash = `Qm${address}`.substring(0, 46)
    return hash
}

/**
 * Decode the Vortex tokenId into the Vortex room name
 * @param {*} tokId 
 * @returns 
 */
export const decodeNameFromTokenId = (tokenId) => {
    let tokenName = ''
    let tokId = new BN(tokenId)
    for (let ix = 31; ix >= 0; ix--) {
        const tokenBits = tokId.shrn(ix * 8).and(new BN('ff', 16))
        if (tokenBits == '0') {
            tokenName += ' '
        } else {
            tokenName += String.fromCharCode(parseInt(tokenBits))
        }
    }
    return tokenName
}

/**
 * Return bytes32 hex string from base58 encoded ipfs hash,
 * stripping leading 2 bytes from 34 byte IPFS hash
 * Assume IPFS defaults: function:0x12=sha2, size:0x20=256 bits
 * E.g. "QmNSUYVKDSvPUnRLKmuxk9diJ6yS96r1TrAXzjTiBcCLAL" -->
 * "0x017dfd85d4f6cb4dcd715a88101f7b1f06cd1e009b2327a0809d01eb9c91f231"

From:
https://ethereum.stackexchange.com/questions/17094/how-to-store-ipfs-hash-using-bytes32
 * @param {*} IPFS version 0 CID 
 * @returns 
 */
export const getBytes32FromIpfsHash = (cid) => {
    return '0x' + bs58.decode(cid).slice(2).toString('hex')
}

/**
 * Convert a version 0 CID to a BigNumber that can then be passed as a uint256 to Solidity.
 * @param {*} cid
 * @param {*} web3
 * @returns
 */
export const getUint32FromCID = (cid) => {
    const web3 = getProvider()
    return web3.utils.toBN(getBytes32FromIpfsHash(cid))
}

export const getUint32ArrayFromCIDs = (cids) => {
    let results = []
    cids.forEach((cid) => {
        results.push(getUint32FromCID(cid, getProvider()))
    })
    return results
}

export const getUint256StringFromCID = (cid) => {
    return new BN(bs58.decode(cid).slice(2).toString('hex'), 16).toString(10)
}
export const getUint256StringFromHex = (value) => {
    return new BN(value, 16).toString(10)
}

export const getUint256StringFromUUID = (uuid) => {
    return new BN(uuid.replaceAll('-', '').toString('hex'), 16).toString(10)
}

export const getCIDFromIpfs = (ipfs) => {
    if (ipfs && ipfs.startsWith('ipfs://')) {
        return ipfs.substring('ipfs://'.length)
    } else {
        return ipfs
    }
}

/**
 * Converts a version 0 CID to a uint256 string
 * @param {*} originalId A CID starting with "Qm"
 * @returns Converted value or originalId if it does not start with "Qm"
 */
export const getTokenId = (originalId) => {
    if (originalId.startsWith('Qm')) {
        return getUint256StringFromCID(originalId)
    } else {
        return originalId
    }
}

//
/** Assuming a version 0 CID decoded as above, return base58 encoded ipfs hash from bytes32 hex string,
 * E.g. "0x017dfd85d4f6cb4dcd715a88101f7b1f06cd1e009b2327a0809d01eb9c91f231"
 * --> "QmNSUYVKDSvPUnRLKmuxk9diJ6yS96r1TrAXzjTiBcCLAL"

 * @param {*} bytes32Hex 
 * @returns IPFS version 0 CID
 */
export const getIpfsHashFromBytes32 = (bytes32Hex) => {
    // Add our default ipfs values for first 2 bytes:
    // function:0x12=sha2, size:0x20=256 bits
    // and cut off leading "0x"
    const hashHex = '1220' + bytes32Hex.slice(2)
    const hashBytes = Buffer.from(hashHex, 'hex')
    const hashStr = bs58.encode(hashBytes)
    return hashStr
}
/**
 * Convert a uint256 to a version 0 IPFS CID.
 *
 * Note that the hash passed to Buffer.from must be an even number of bytes
 * or it crashes.
 *
 * @param {*} solInt A Solidity uin256
 * @returns
 */
export const getCIDFromUint256 = (solInt) => {
    //return getIpfsHashFromBytes32(`0x${new BN(solInt).toString(16)}`)
    let wht = new BN(solInt).toString(16)
    if (wht.length % 2 !== 0) {
        wht = '0' + wht
    }
    const hashHex = '1220' + wht //new BN(solInt).toString(16)
    const hashBytes = Buffer.from(hashHex, 'hex')
    const hashStr = bs58.encode(hashBytes)
    return hashStr
}
const ERROR_MAPPING = [
    { code: '[e1]', message: 'The mint must provide theSHA256 hash of the streaming media associated with this NFT' },
    { code: '[e2]', message: 'NFT price exceeds maximum allowed' },
    { code: '[e3]', message:'A partner address is 0, or this contract, or the the minter'},
    { code: '[e4]', message:'An address has been specified more than once in the partners array'},
    { code: '[e10]', message: 'Insufficient msg.value to pay mint fee' },
    { code: '[e30]', message: 'Msg.sender is not token owner' },
    {
        code: '[e31]',
        message:
            'Specified price is less than the minimum token sale price in an issue',
    },
    { code: '[e35]', message: 'Insufficient msg.value to pay tx fee' },
    {
        code: '[e40]',
        message:
            'Either to address is the contract, or msg.value is less than the token price',
    },
    {
        code: '[e50]',
        message:
            'Token cannot be transferred, either because there are partners or royalties',
    },
    { code: '[pr1]', message: 'Msg.sender is not token owner' },
    {
        code: '[pr2]',
        message: 'No additional issue is permitted',
    },
    {
        code: '[pr3]',
        message:
            'Error in issue (shares offered, minTokenSalePrice, sharePrice, totalSharePercent)',
    },
    {
        code: '[pr5]',
        message: 'Maximum number of terms exceeded',
    },
    {
        code: '[pr20]',
        message: 'Attempt to buy token shares when issue is not open',
    },
    { code: '[pr21]', message: 'Attempt to buy more shares than available' },
    { code: '[pr22]', message: 'Maximum number of partners would be exceeded' },
    { code: '[pr23]', message: 'Insufficient msg.value to buy issued shares' },
    { code: '[pr40]', message: 'Attempt to sell more partner shares than owned' },
    {
        code: '[pr51]',
        message: 'Insufficient msg.value to make partner distribution',
    },
    {
        code: '[pr60]',
        message: 'Attempt to buy more partner shares than available',
    },
    { code: '[pr61]', message: 'Insufficient msg.value to buy partner shares' },
]

/**
 * Process a transaction error message.
 *
 * If it includes user denied or rejected, it is ignored on the
 * assumption this is a Reject message from Metamask.
 *
 * If it includes 'insufficient funds' we return a simple message.
 *
 * Otherwise we search ERROR_MAPPING and
 * PAYMENT_TYPES.
 *
 * If error.message does not exist, we stringify the error and return that.
 * @param {*} error
 * @param {*} setError
 * @returns true if this was a user reject, false otherwise
 */
export const getDisplayError = (error, setError) => {
    console.log('getDisplayError', error)
    const { message } = error
    const messageToParse = message ? message : error
    let codeMessage
    if (messageToParse) {
        const msgLower = messageToParse.toLowerCase()
        if (
            msgLower.includes('user denied') ||
            msgLower.includes('user rejected')
        ) {
            setError('')
            return true
        } else if (msgLower.startsWith('transaction failed')) {
            //This is a bail out because we can't get the GD revert messages anymore
            setError(message)
        } else if (msgLower.includes('insufficient funds')) {
            setError('You do not have enough funds to complete this transaction')
        } else if (msgLower.includes('token already minted')) {
            setError('This NFT has already been minted')
        } else if (
            (codeMessage = ERROR_MAPPING.find((e) => msgLower.includes(e.code)))
        ) {
            setError(codeMessage.message)
        } else if (
            (codeMessage = PAYMENT_TYPES.find((e) => msgLower.includes(e.code)))
        ) {
            setError(codeMessage.message)
        } else {
            setError(messageToParse)
        }
    } else {
        setError(JSON.stringify(error))
    }
    return false
}
/**
 * Gets a microether-based fee
 * @param {*} options
 * @param {*} shift
 * @param {*} mask
 * @returns
 */
const getFee = (options, shift, mask) => {
    const currentOptions = new BN(options)
    const fee = currentOptions.shrn(shift).and(new BN(mask, 16)).toNumber()
    return fee
}

/**
 * @param {*} options
 * @param {*} feeType 0=transactionFee 1=purchaseFee
 */
export const getOptionsFee = (options, feeType) => {
    switch (feeType) {
        case 0:
            return (
                getFee(
                    options,
                    OPTIONS_TRANSACTION_FEE_SHIFT,
                    OPTIONS_TRANSACTION_FEE_MASK
                ) *
                10 ** 12
            )
        case 1:
            return (
                getFee(options, OPTIONS_PURCHASE_FEE_SHIFT, OPTIONS_PURCHASE_FEE_MASK) /
                10
            )
    }
}

/**
 *
 * @param {*} options
 * @param {*} fee
 * @param {*} shift
 * @param {*} mask
 * @returns
 */
const setFee = (options, fee, shift, mask) => {
    const currentOptions = new BN(options)
    const newFee = new BN(fee).and(new BN(mask, 16)).shln(shift)
    //console.log(`shifted transactionFee ${fee.toString(16)}`)
    const resetBits = new BN(mask, 16).shln(shift).notn(256)
    const newOptions = currentOptions.and(resetBits).or(newFee)
    console.log(`options with fee ${newOptions.toString(16)}`)
    return newOptions
}

/**
 * Gets field from the bitmap
 * @param {*} bitmap    A uint256 bitmap
 * @param {*} shift     Bits to shift right to start of bitmap field
 * @param {*} mask      Mask to throw away unused bits
 * @returns             A BN
 */
export const getBitmapValue = (bitmap, shift, mask) => {
    const currentBitmap = new BN(bitmap)
    const value = currentBitmap.shrn(shift).and(new BN(mask, 16))
    return value
}
/**
   *Watch the fuck out for this:
  
    If the mask is a string literal (because it is too long to be represented as a JS num) then
    do *not* include the 0x prefix or the BN library considers that part of the mask.
  
   * @param {*} options
   * @param {*} value
   * @param {*} shift
   * @param {*} mask
   * @returns
   */
export const setBitmapValue = (options, value, shift, mask) => {
    const currentValue = new BN(options)
    const newValue = new BN(value).and(new BN(mask, 16)).shln(shift)
    const resetBits = new BN(mask, 16).shln(shift).notn(256)
    return currentValue.and(resetBits).or(newValue)
}

/**
 *
 * @param {*} options
 * @param {*} fee     0: 20bit microether 1: floating point, 1 decimal place 
 * @param {*} feeType 0: transactionFee, flat fee in microether 
 * 1: purchaseFee, 10 bit percentage with 1 implied decimal point
 * @returns
 */
export const setOptionsFee = (options, fee, feeType) => {
    switch (feeType) {
        case 0:
            return setFee(
                options,
                fee,
                OPTIONS_TRANSACTION_FEE_SHIFT,
                OPTIONS_TRANSACTION_FEE_MASK
            )
        case 1:
            return setFee(
                options,
                Math.round(fee * 10),
                OPTIONS_PURCHASE_FEE_SHIFT,
                OPTIONS_PURCHASE_FEE_MASK
            )

    }
}
/**
 * Get the mintFee in microether, multiple by 10**12 to get wei.
 * @param {*} options
 */
export const getOptionsMintFee = (options) => {
    const currentOptions = new BN(options)
    const shift = OPTIONS_MINT_FEE_SHIFT
    const mask = OPTIONS_MINT_FEE_MASK
    const fee = currentOptions.shrn(shift).and(new BN(mask, 16)).toNumber()
    return fee * 10 ** 12
}

/** Set the mint fee in microether (szabo) into bits of current options
 *
 * @param {*} options Options as a uint256 string
 * @param {*} mintFee Mint fee as a hex string. Only 20 bits are used
 */
export const setOptionsMintFee = (options, mintFee) => {
    const currentOptions = new BN(options)
    console.log(
        `setOptionsMintFee mintFee 0x${mintFee} options 0x${currentOptions.toString(
            16
        )}`
    )
    const shift = OPTIONS_MINT_FEE_SHIFT
    const mask = OPTIONS_MINT_FEE_MASK
    const fee = new BN(mintFee, 16).shln(shift)
    console.log(`shifted mint fee ${fee.toString(16)}`)
    const resetBits = new BN(mask, 16).shln(shift).notn(256)
    const newOptions = currentOptions.and(resetBits).or(fee)
    console.log(`options with mint fee 0x${newOptions.toString(16)}`)
    return newOptions
}
export const mintFeeInMicroether = (mintFee) => {
    return new BN(mintFee / 10 ** 12).toString(10)
}

export const getOptionsAllowRoyalty = (options) => {
    const currentOptions = new BN(options)
    return currentOptions.testn(OPTIONS_ALLOW_CREATOR_ROYALTY_BIT)
}
/**
 * It appears that BN counts bits starting at 0. The 0th bit of options is set to 1
 * if the contract is paused.
 * @param {*} options Options as a uint256 string
 * @returns True if running (bit is 0)
 */
export const getOptionsIsRunning = (options) => {
    const currentOptions = new BN(options)
    /*
    for (let i = 0; i < 20; i++) {
      console.log(`at ${i} ${currentOptions.testn(i)}`)
    }
  */
    return !currentOptions.testn(OPTIONS_PAUSED_BIT)
}

/**
 * If running is true, set paused bit to 0, otherwise 1
 * @param {*} options Options as a uint256 string
 * @param {*} running bool true or false
 * @returns
 */
export const setOptionsIsRunning = (options, running) => {
    const currentOptions = new BN(options)
    console.log(`current options ${currentOptions.toString(16)}`)
    currentOptions.setn(0, !running)
    console.log(
        `current options running ${running} ${currentOptions.toString(16)}`
    )
    return currentOptions
}

export const getOptionsMaxTokensPerMint = (options) => {
    return getOptionsFee(options, 2)
}
export const setOptionsMaxTokensPerMint = (options, maxTokens) => {
    return setOptionsFee(options, maxTokens, 2)
}

const getPriceOption = (price, shift, mask) => {
    const currentPrice = new BN(price)
    const option = currentPrice.shrn(shift).and(new BN(mask, 16)).toNumber()
    return option
}

export const getPriceOnly = (price) => {
    return getPriceOption(price, 0, PRICE_MASK)
}

export const getTokenPlatform = (price) => {
    return getPriceOption(price, PLATFORM_SHIFT, PLATFORM_MASK)
}


export const getTokenHasPrivileges = (price) => {
    return getPriceOption(price, PRIVILEGES_SHIFT, PRIVILEGES_MASK) === 1
}

/**
 * Set the TOKEN_HAS_PRIVILEGES bit on the price
 * @param {*} price
 * @returns
 */
export const setTokenHasPrivileges = (price) => {
    const priceWithPriv = setBitmapValue(
        price,
        TOKEN_HAS_PRIVILEGES,
        PRIVILEGES_SHIFT,
        PRIVILEGES_MASK
    )
    return priceWithPriv
}

export const getTokenIsAlbum = (price) => {
    return getPriceOption(price, TOKEN_TYPE_SHIFT, TOKEN_TYPE_MASK) === 1
}

/**
 * Set the TOKEN_TYPE_ALBUM bits on the price
 * @param {*} price
 * @returns
 */
export const setTokenTypeAlbum = (price) => {
    return setBitmapValue(
        price,
        TOKEN_TYPE_ALBUM,
        TOKEN_TYPE_SHIFT,
        TOKEN_TYPE_MASK
    )
}

/** The price stored in the contract PriceAndIssues field is in gwei, and only occupies
 * lower-order bits. This converts that field to a price in wei
 * @returns the price portion of the price stored in the contract */
export const getWeiFromPrice = (priceAndIssue) => {
    const { price } = priceAndIssue
    return new BN(price)
        .and(new BN(PRICE_MASK, 16))
        .mul(new BN(10 ** 9))
        .toString(10)
}

export const getEthFromGwei = (priceInGwei) => {
    return new BN(priceInGwei) / new BN(10 ** 9)
}


/* ********************* */

/**
 *
 * @param {*} numSharesIssued
 * @param {*} sharePercentage         Integer, multiplied by 10 in method to get 1 implied decimal place
 * @param {*} issueSharePrice         Share price in eth
 * @param {*} minTokenSalePrice       Minimum token sale price in eth
 */
export const createIssue = (
    numSharesIssued,
    sharePercentage,
    issueSharePrice,
    minTokenSalePrice
) => {
    let offerIssue = new BN(OFFERING_STATE_OPEN)
    const sharePriceGwei = issueSharePrice * 10 ** 9
    const minTokenSalePriceGwei = minTokenSalePrice * 10 ** 9
    offerIssue = setBitmapValue(
        offerIssue,
        numSharesIssued,
        SHARES_OFFERED_SHIFT,
        SHARES_OFFERED_MASK
    )

    offerIssue = setBitmapValue(
        offerIssue,
        sharePercentage * 10,
        TOTAL_SHARE_PERCENT_SHIFT,
        TOTAL_SHARE_PERCENT_MASK
    )

    offerIssue = setBitmapValue(
        offerIssue,
        sharePriceGwei,
        SHARE_PRICE_SHIFT,
        SHARE_PRICE_MASK
    )

    offerIssue = setBitmapValue(
        offerIssue,
        minTokenSalePriceGwei,
        MIN_TOKEN_SALE_PRICE_SHIFT,
        MIN_TOKEN_SALE_PRICE_MASK
    )
    console.log(`created issue ${new BN(offerIssue).toString(16)}`)
    return offerIssue
}